import React, { useState, useEffect } from 'react';
import ConferenceCalendar from '../ConferenceCalendar';
import MultiSpeakerSelector from './MultiSpeakerSelector';
import { Session, Speaker } from '../../types/conference';

interface SessionManagementProps {
  sessions: Session[];
  speakers: Speaker[];
  onCreateSession: (session: Omit<Session, 'id'>) => Promise<void>;
  onUpdateSession: (session: Session) => Promise<void>;
  onDeleteSession: (sessionId: string) => Promise<void>;
  prefilledSession?: Partial<Session>;
}

interface Event {
  id: string;
  title: string;
  start: Date;
  end: Date;
  type: 'talk' | 'workshop' | 'networking' | 'break';
  location: string;
  speakers: Speaker[];
}

const SessionManagement: React.FC<SessionManagementProps> = ({
  sessions,
  speakers,
  onCreateSession,
  onUpdateSession,
  onDeleteSession,
  prefilledSession
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [editingSession, setEditingSession] = useState<Session | null>(null);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [view, setView] = useState<'calendar' | 'list'>('calendar');
  const [selectedSpeakers, setSelectedSpeakers] = useState<Speaker[]>([]);

  useEffect(() => {
    if (prefilledSession) {
      setIsModalOpen(true);
      // Set speakers from prefilled session
      if (prefilledSession.speakers) {
        setSelectedSpeakers(prefilledSession.speakers);
      } else if (prefilledSession.speaker) {
        setSelectedSpeakers([prefilledSession.speaker]);
      }
    }
  }, [prefilledSession]);

  useEffect(() => {
    // Update selected speakers when editing session changes
    if (editingSession) {
      if (editingSession.speakers) {
        setSelectedSpeakers(editingSession.speakers);
      } else if (editingSession.speaker) {
        setSelectedSpeakers([editingSession.speaker]);
      } else {
        setSelectedSpeakers([]);
      }
    } else {
      setSelectedSpeakers([]);
    }
  }, [editingSession]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    // Build session data with multi-speaker support
    const sessionData = {
      title: formData.get('title') as string,
      description: formData.get('description') as string,
      type: formData.get('type') as Session['type'],
      speakers: selectedSpeakers.length > 0 ? selectedSpeakers : undefined,
      speaker: selectedSpeakers.length > 0 ? selectedSpeakers[0] : undefined, // Backward compatibility
      startTime: formData.get('startTime') as string,
      endTime: formData.get('endTime') as string,
      room: formData.get('room') as string,
      capacity: formData.get('capacity') ? parseInt(formData.get('capacity') as string) : undefined,
      status: formData.get('status') as 'scheduled' | 'cancelled' | 'completed',
    };

    if (editingSession) {
      await onUpdateSession({ ...sessionData, id: editingSession.id } as Session);
    } else {
      await onCreateSession(sessionData as Omit<Session, 'id'>);
    }

    setIsModalOpen(false);
    setEditingSession(null);
    setSelectedSpeakers([]);
  };

  // Log all events to compare
  console.log('All sessions:', sessions);

  const calendarEvents: Event[] = sessions.map(session => {
    // Parse dates and ensure they're treated as local time
    const start = new Date(session.startTime);
    const end = new Date(session.endTime);
    
    const event = {
      id: session.id,
      title: session.title,
      start,
      end,
      type: session.type,
      location: session.room || 'TBD',
      speakers: session.speakers || (session.speaker ? [session.speaker] : []),
    };

    // Log each event as it's created
    // console.log('Created event:', {
    //   id: event.id,
    //   title: event.title,
    //   type: event.type,
    //   start: event.start.toISOString(),
    //   typeCheck: {
    //     isNetworking: event.type === 'networking',
    //     isTalk: event.type === 'talk',
    //     typeValue: event.type,
    //     typeType: typeof event.type
    //   }
    // });

    return event;
  });

  // Log final calendar events
  // console.log('Calendar events:', calendarEvents);

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <div className="flex space-x-4">
          <button
            onClick={() => setView('calendar')}
            className={`px-3 py-2 rounded-md text-sm font-medium ${
              view === 'calendar'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Calendar View
          </button>
          <button
            onClick={() => setView('list')}
            className={`px-3 py-2 rounded-md text-sm font-medium ${
              view === 'list'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            List View
          </button>
        </div>
        <button
          onClick={() => {
            setEditingSession(null);
            setIsModalOpen(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Add Session
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {view === 'calendar' ? (
          <ConferenceCalendar
            events={calendarEvents}
            onEventClick={(event) => {
              const session = sessions.find(s => s.id === event.id);
              if (session) {
                setSelectedSession(session);
                setIsDetailsModalOpen(true);
              }
            }}
            isEditable={true}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Speaker
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Room
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sessions.map((session) => (
                  <tr key={session.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {session.title}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {session.speakers && session.speakers.length > 0 
                          ? session.speakers.map(s => s.name).join(', ')
                          : session.speaker?.name || 'No speaker'
                        }
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {new Date(session.startTime).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{session.room}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          session.status === 'scheduled'
                            ? 'bg-green-100 text-green-800'
                            : session.status === 'cancelled'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {session.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => {
                          console.log('Before setting editingSession, session is:', session);
                          console.log('Speaker in session:', session.speaker);
                          setEditingSession(session);
                          setIsModalOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => onDeleteSession(session.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Session Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              {editingSession ? 'Edit Session' : 'Add Session'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  Title
                </label>
                <input
                  type="text"
                  name="title"
                  id="title"
                  required
                  defaultValue={editingSession?.title || prefilledSession?.title || ''}
                  className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  name="description"
                  id="description"
                  required
                  rows={4}
                  defaultValue={editingSession?.description || prefilledSession?.description || ''}
                  className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                  Type
                </label>
                <select
                  name="type"
                  id="type"
                  required
                  defaultValue={editingSession?.type || prefilledSession?.type || ''}
                  className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="talk">Talk</option>
                  <option value="workshop">Workshop</option>
                  <option value="networking">Networking</option>
                  <option value="break">Break</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Speakers
                </label>
                <MultiSpeakerSelector
                  speakers={speakers}
                  selectedSpeakers={selectedSpeakers}
                  onSpeakersChange={setSelectedSpeakers}
                  maxSpeakers={10}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="startTime" className="block text-sm font-medium text-gray-700">
                    Start Time
                  </label>
                  <input
                    type="datetime-local"
                    name="startTime"
                    id="startTime"
                    required
                    defaultValue={editingSession?.startTime || prefilledSession?.startTime || ''}
                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="endTime" className="block text-sm font-medium text-gray-700">
                    End Time
                  </label>
                  <input
                    type="datetime-local"
                    name="endTime"
                    id="endTime"
                    required
                    defaultValue={editingSession?.endTime || prefilledSession?.endTime || ''}
                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="room" className="block text-sm font-medium text-gray-700">
                    Room
                  </label>
                  <input
                    type="text"
                    name="room"
                    id="room"
                    defaultValue={editingSession?.room || prefilledSession?.room || ''}
                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="capacity" className="block text-sm font-medium text-gray-700">
                    Capacity
                  </label>
                  <input
                    type="number"
                    name="capacity"
                    id="capacity"
                    min="1"
                    defaultValue={editingSession?.capacity || prefilledSession?.capacity || ''}
                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  name="status"
                  id="status"
                  defaultValue={editingSession?.status || prefilledSession?.status || 'scheduled'}
                  className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div className="mt-5 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingSession(null);
                  }}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  {editingSession ? 'Save Changes' : 'Create Session'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Details Modal */}
      {isDetailsModalOpen && selectedSession && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center" style={{ zIndex: 1000 }}>
          <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full m-4">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{selectedSession.title}</h3>
              <div className="space-y-3">
                <p><span className="font-medium">Type:</span> {selectedSession.type}</p>
                <p><span className="font-medium">Speakers:</span> {
                  selectedSession.speakers && selectedSession.speakers.length > 0 
                    ? selectedSession.speakers.map(s => s.name).join(', ')
                    : selectedSession.speaker?.name || 'N/A'
                }</p>
                <p><span className="font-medium">Time:</span> {new Date(selectedSession.startTime).toLocaleString()}</p>
                <p><span className="font-medium">Room:</span> {selectedSession.room || 'TBD'}</p>
                <p><span className="font-medium">Status:</span> {selectedSession.status}</p>
                {selectedSession.description && (
                  <p><span className="font-medium">Description:</span> {selectedSession.description}</p>
                )}
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setIsDetailsModalOpen(false);
                    setSelectedSession(null);
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setIsDetailsModalOpen(false);
                    setEditingSession(selectedSession);
                    setIsModalOpen(true);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionManagement;
